import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, Pagination, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, InputAdornment,
  Tabs, Tab, MenuItem, Divider,
} from '@mui/material';
import { Add, Search, Payment, AccountBalance, OpenInNew } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

function SupplierDetailDialog({ supplier, open, onClose, dispatch, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState(0);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('cash');
  const [payNote, setPayNote] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (open && supplier) {
      api.get(`/suppliers/${supplier.id}`).then(r => setDetail(r.data.data));
    }
  }, [open, supplier]);

  const recordPayment = async () => {
    if (!payAmount || +payAmount <= 0) return;
    setPaying(true);
    try {
      await api.post(`/suppliers/${supplier.id}/payment`, { amount: +payAmount, method: payMethod, description: payNote || 'Payment to supplier' });
      dispatch(showSnackbar({ message: 'Payment recorded', severity: 'success' }));
      api.get(`/suppliers/${supplier.id}`).then(r => setDetail(r.data.data));
      setPayAmount(''); setPayNote('');
      onRefresh();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    } finally { setPaying(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {supplier?.name}
        {detail?.outstanding_balance > 0 && <Chip label={`Due: ₨ ${fmt(detail.outstanding_balance)}`} color="error" size="small" sx={{ ml: 1 }} />}
        {detail?.outstanding_balance === 0 && <Chip label="Clear" color="success" size="small" sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent>
        {!detail ? <CircularProgress /> : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Payments" />
              <Tab label="Purchases" />
            </Tabs>

            {tab === 0 && (
              <>
                {detail.outstanding_balance > 0 && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField size="small" label="Amount ₨" type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} sx={{ width: 160 }} />
                    <TextField size="small" select label="Method" value={payMethod} onChange={e => setPayMethod(e.target.value)} sx={{ width: 150 }}>
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="cheque">Cheque</MenuItem>
                    </TextField>
                    <TextField size="small" label="Note" value={payNote} onChange={e => setPayNote(e.target.value)} sx={{ flex: 1 }} />
                    <Button variant="contained" color="success" onClick={recordPayment} disabled={!payAmount || paying} startIcon={<Payment />}>
                      {paying ? <CircularProgress size={18} /> : 'Pay Supplier'}
                    </Button>
                  </Box>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.payments || []).map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{new Date(p.entry_date || p.created_at).toLocaleDateString('en-PK')}</TableCell>
                        <TableCell>{p.description}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>₨ {fmt(p.amount)}</TableCell>
                        <TableCell align="right">₨ {fmt(p.balance_after)}</TableCell>
                      </TableRow>
                    ))}
                    {detail.payments?.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No payments yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </>
            )}

            {tab === 1 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Purchase #</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Due</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.purchases || []).map(p => (
                    <TableRow key={p.id}>
                      <TableCell>#{p.id}</TableCell>
                      <TableCell align="right">₨ {fmt(p.total_amount)}</TableCell>
                      <TableCell align="right">₨ {fmt(p.paid_amount)}</TableCell>
                      <TableCell align="right" sx={{ color: p.due_amount > 0 ? 'error.main' : 'inherit' }}>₨ {fmt(p.due_amount)}</TableCell>
                      <TableCell><Chip label={p.payment_status} size="small" color={{ paid: 'success', partial: 'warning', due: 'error' }[p.payment_status] || 'default'} /></TableCell>
                      <TableCell><Typography variant="caption">{new Date(p.created_at).toLocaleDateString('en-PK')}</Typography></TableCell>
                    </TableRow>
                  ))}
                  {detail.purchases?.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>No purchases</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SuppliersPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailSupplier, setDetailSupplier] = useState(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/suppliers', { params: { page, limit: 15, search } });
      setSuppliers(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (s = null) => { setEditing(s); reset(s || {}); setDialogOpen(true); };

  const onSubmit = async (data) => {
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, data);
      else await api.post('/suppliers', data);
      dispatch(showSnackbar({ message: editing ? 'Supplier updated' : 'Supplier added', severity: 'success' }));
      setDialogOpen(false); load();
    } catch (e) {
      dispatch(showSnackbar({ message: 'Error', severity: 'error' }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.suppliers')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>Add Supplier</Button>
      </Box>

      <Card>
        <CardContent>
          <TextField
            placeholder={t('common.search')} size="small" sx={{ mb: 2, width: 320 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />

          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>{t('common.phone')}</TableCell>
                <TableCell>{t('common.city')}</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {suppliers.map(s => (
                  <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailSupplier(s)}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{s.name}</Typography></TableCell>
                    <TableCell>{s.company}</TableCell>
                    <TableCell>{s.phone}</TableCell>
                    <TableCell>{s.city}</TableCell>
                    <TableCell align="right">
                      {s.outstanding_balance > 0
                        ? <Chip label={`₨ ${fmt(s.outstanding_balance)}`} size="small" color="error" />
                        : <Chip label="Clear" size="small" color="success" />}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button size="small" onClick={() => openDialog(s)}>Edit</Button>
                      <Button size="small" startIcon={<OpenInNew />} onClick={() => navigate(`/suppliers/${s.id}`)}>View</Button>
                      {s.outstanding_balance > 0 && (
                        <Button size="small" color="success" startIcon={<Payment />} onClick={() => setDetailSupplier(s)}>Pay</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
        <DialogContent>
          <Box component="form" id="sup-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}><TextField fullWidth label="Name *" {...register('name', { required: true })} error={!!errors.name} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Company" {...register('company')} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Email" {...register('email')} /></Grid>
              <Grid item xs={8}><TextField fullWidth label="Address" {...register('address')} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="City" {...register('city')} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Notes" {...register('notes')} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="sup-form">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <SupplierDetailDialog
        supplier={detailSupplier}
        open={!!detailSupplier}
        onClose={() => setDetailSupplier(null)}
        dispatch={dispatch}
        onRefresh={load}
      />
    </Box>
  );
}
